import React, { useCallback, useRef } from 'react';
import { useReactFlow, useStoreApi, type XYPosition } from 'reactflow'; // Changed import path

// Define the structure for control point data, matching EdgeData.points
export interface ControlPointData extends XYPosition {
  id: string;
  active: boolean; // Indicates if the point is part of the current path calculation
  prev?: ControlPointData; // Reference to the previous point (if any)
}

interface ControlPointProps extends ControlPointData {
  index: number;
  setControlPoints: (update: (points: ControlPointData[]) => ControlPointData[]) => void;
  color: string; // Color for the control point
  onDragEnd?: () => void; // Optional callback for drag end
}

// Basic styles for the control point handle
const controlPointStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  border: '2px solid',
  position: 'absolute',
  cursor: 'move',
  pointerEvents: 'all', // Ensure it's interactive
  transform: 'translate(-50%, -50%)', // Center the handle on its coordinates
  zIndex: 10, // Ensure it's above the edge path
};

export function ControlPoint({
  id,
  x,
  y,
  index,
  active,
  setControlPoints,
  color,
  onDragEnd: onDragEndProp, // Rename the prop to avoid conflict
}: ControlPointProps) {
  const store = useStoreApi();
  const { project } = useReactFlow();
  const dragRef = useRef<HTMLDivElement>(null);

  const onDragStart = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData('application/reactflow', 'control-point');
    event.dataTransfer.effectAllowed = 'move';
    // Optional: Add a class for styling during drag
    dragRef.current?.classList.add('dragging');
  }, []);

  const onDrag = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    // Prevent default to allow drop
    event.preventDefault();
    // Calculate new position based on drag event and projection
    const { domNode } = store.getState();
    if (!domNode) return;

    const bounds = domNode.getBoundingClientRect();
    const position = project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });

    // Get the source and target nodes to calculate relative position
    const { edges, nodeInternals } = store.getState();
    const edge = edges.find(e => e.id === id.split('-')[0]); // Assuming control point id starts with edge id
    
    if (!edge) return;
    
    const sourceNode = nodeInternals.get(edge.source);
    const targetNode = nodeInternals.get(edge.target);
    
    if (!sourceNode || !targetNode) return;
    
    // Calculate source and target positions
    const sourcePos = {
      x: sourceNode.position.x + (sourceNode.width || 0) / 2,
      y: sourceNode.position.y + (sourceNode.height || 0) / 2
    };
    
    const targetPos = {
      x: targetNode.position.x + (targetNode.width || 0) / 2,
      y: targetNode.position.y + (targetNode.height || 0) / 2
    };
    
    // Calculate the total distance between source and target
    const totalDistance = Math.sqrt(
      Math.pow(targetPos.x - sourcePos.x, 2) + 
      Math.pow(targetPos.y - sourcePos.y, 2)
    );
    
    // Calculate the distance from source to the current position
    const currentDistance = Math.sqrt(
      Math.pow(position.x - sourcePos.x, 2) + 
      Math.pow(position.y - sourcePos.y, 2)
    );
    
    // Calculate the relative position (0-100)
    const relativePosition = Math.min(100, Math.max(0, (currentDistance / totalDistance) * 100));
    
    // Update the specific control point's position with the relative value
    setControlPoints((points) =>
      points.map((p, i) => (i === index ? { ...p, x: relativePosition, y: 0 } : p))
    );
  }, [project, store, index, setControlPoints, id]);

  const onDragEnd = useCallback(() => {
    // Optional: Remove dragging class
    dragRef.current?.classList.remove('dragging');
    // Call the passed-in onDragEnd prop if it exists
    onDragEndProp?.();
  }, [onDragEndProp]); // Add prop to dependency array

  // Double-click to toggle active state (similar to example)
  const onDoubleClick = useCallback(() => {
    setControlPoints((points) =>
      points.map((p, i) => (i === index ? { ...p, active: !p.active } : p))
    );
  }, [index, setControlPoints]);

  return (
    <div
      ref={dragRef}
      style={{
        ...controlPointStyle,
        left: x,
        top: y,
        borderColor: color,
        backgroundColor: active ? color : 'white', // Fill if active
        opacity: active ? 1 : 0.5, // Dim if inactive
      }}
      draggable
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onDoubleClick={onDoubleClick}
      className="nodrag" // Prevent React Flow from dragging the node/pane when dragging the handle
    />
  );
}
